import type { Language } from "../../../types/i18n";

// Part of the healthMore module (see ../healthMore.ts). English is the
// reference; every other language must carry the same keys.
// Prefixes: xvital (vital signs), emc (emergency contacts),
// recs (health recommendations), outbreak (disease outbreaks).
const en = {
  // Extended vital signs
  xvitalTitle: "Extended Vital Signs",
  xvitalDateLabel: "Measurement date *",
  xvitalRecordedBy: "Measured by",
  xvitalRecordedByPlaceholder: "Name of the person measuring",
  xvitalTemperature: "Body temperature",
  xvitalValue: "Value",
  xvitalSiteOral: "Oral",
  xvitalSiteEar: "Ear",
  xvitalSiteForehead: "Forehead",
  xvitalSiteArmpit: "Armpit",
  xvitalSiteRectal: "Rectal",
  xvitalRespRate: "Respiratory rate",
  xvitalBreathsPerMin: "breaths/min",
  xvitalMethodObserved: "Observed",
  xvitalMethodMeasured: "Measured",
  xvitalMeasuredSuffix: "measured",
  xvitalO2Sat: "Oxygen saturation",
  xvitalRoomAir: "Room air (no supplemental oxygen)",
  xvitalO2Flow: "O2 flow (L/min)",
  xvitalGlucose: "Blood glucose",
  xvitalGlucoseAbbr: "Glucose",
  xvitalTimingFasting: "Fasting",
  xvitalTimingRandom: "Random",
  xvitalTimingPostprandial: "After a meal",
  xvitalTimingBedtime: "Bedtime",
  xvitalPainLevel: "Pain level (0–10)",
  xvitalPain: "Pain",
  xvitalPainLocation: "Pain location",
  xvitalPainQualitySelect: "Select pain quality",
  xvitalPainSharp: "Sharp",
  xvitalPainDull: "Dull",
  xvitalPainBurning: "Burning",
  xvitalPainThrobbing: "Throbbing",
  xvitalPainCramping: "Cramping",
  xvitalPainAching: "Aching",
  xvitalMentalStatus: "Mental status",
  xvitalAlertness: "Alertness",
  xvitalAlert: "Alert",
  xvitalLethargic: "Lethargic",
  xvitalObtunded: "Obtunded",
  xvitalStuporous: "Stuporous",
  xvitalComatose: "Comatose",
  xvitalMood: "Mood",
  xvitalMoodNormal: "Normal",
  xvitalMoodAnxious: "Anxious",
  xvitalMoodDepressed: "Depressed",
  xvitalMoodAgitated: "Agitated",
  xvitalMoodEuphoric: "Euphoric",
  xvitalMoodOther: "Other",
  xvitalOrientation: "Orientation",
  xvitalOrientPerson: "Person",
  xvitalOrientPlace: "Place",
  xvitalOrientTime: "Time",
  xvitalOrientSituation: "Situation",
  xvitalNotes: "Additional notes",
  xvitalNotesPlaceholder:
    "Special observations, circumstances of the measurement, etc.",
  xvitalRemove: "Remove measurement",
  xvitalAdd: "Add new vital signs measurement",
  xvitalEmpty: "No extended vital signs recorded",
  xvitalMeasurementOf: "Measurement of {date}",
  xvitalBy: "by {name}",
  xvitalBpOrder: "Systolic value must be higher than diastolic value",
  xvitalHeartRatePlaceholder: "e.g. 72",

  // Emergency contacts
  emcTitle: "Emergency Contacts",
  emcRelSpouse: "Spouse",
  emcRelPartner: "Partner",
  emcRelParent: "Parent",
  emcRelChild: "Child",
  emcRelSibling: "Sibling",
  emcRelGuardian: "Guardian",
  emcRelFriend: "Friend",
  emcRelOther: "Other",
  emcPrimaryLabel: "Primary contact (notified first)",
  emcRemove: "Remove contact",
  emcNameLabel: "Name *",
  emcNamePlaceholder: "Full name",
  emcRelationshipLabel: "Relationship *",
  emcRelationshipSpecify: "Specify relationship",
  emcRelationshipPlaceholder: "e.g. neighbor, coworker",
  emcPhoneLabel: "Phone number *",
  emcPhonePlaceholder: "+1 (555) 123-4567",
  emcAltPhoneLabel: "Alternative phone number",
  emcAltPhonePlaceholder: "Second number (optional)",
  emcEmailLabel: "Email",
  emcLanguageLabel: "Language",
  emcCanDecide: "May make medical decisions",
  emcAddressLabel: "Address (optional)",
  emcStreetPlaceholder: "Street address",
  emcCityPlaceholder: "City",
  emcPostalPlaceholder: "ZIP code",
  emcCountryPlaceholder: "Country",
  emcNotesLabel: "Additional notes",
  emcNotesPlaceholder: "Special information about this contact",
  emcAdd: "Add new emergency contact",
  emcEmpty: "No emergency contacts on file",

  // Health recommendations
  recsSeverityCritical: "Critical",
  recsSeverityHigh: "High",
  recsSeverityMedium: "Medium",
  recsSeverityLow: "Low",
  recsSeverityInfo: "Info",
  recsRiskLow: "Low",
  recsRiskModerate: "Moderate",
  recsRiskHigh: "High",
  recsRiskCritical: "Critical",
  recsTravelCruise: "Cruise",
  recsTravelFlight: "Flight",
  recsTravelLand: "Overland",
  recsTravelMixed: "Mixed",

  // Disease outbreaks
  outbreakSeverityCritical: "Critical",
  outbreakSeverityHigh: "High",
  outbreakSeverityMedium: "Medium",
  outbreakSeverityLow: "Low",
};

export const vitalsStrings: Record<Language, typeof en> = {
  en,
  de: {
    xvitalTitle: "Erweiterte Vitalzeichen",
    xvitalDateLabel: "Messdatum *",
    xvitalRecordedBy: "Gemessen von",
    xvitalRecordedByPlaceholder: "Name des Messenden",
    xvitalTemperature: "Körpertemperatur",
    xvitalValue: "Wert",
    xvitalSiteOral: "Oral",
    xvitalSiteEar: "Ohr",
    xvitalSiteForehead: "Stirn",
    xvitalSiteArmpit: "Achsel",
    xvitalSiteRectal: "Rektal",
    xvitalRespRate: "Atemfrequenz",
    xvitalBreathsPerMin: "Atemzüge/min",
    xvitalMethodObserved: "Beobachtet",
    xvitalMethodMeasured: "Gemessen",
    xvitalMeasuredSuffix: "gemessen",
    xvitalO2Sat: "Sauerstoffsättigung",
    xvitalRoomAir: "Raumluft (ohne O2-Gabe)",
    xvitalO2Flow: "O2-Fluss (L/min)",
    xvitalGlucose: "Blutzucker",
    xvitalGlucoseAbbr: "BZ",
    xvitalTimingFasting: "Nüchtern",
    xvitalTimingRandom: "Gelegenheit",
    xvitalTimingPostprandial: "Nach Mahlzeit",
    xvitalTimingBedtime: "Vor dem Schlafen",
    xvitalPainLevel: "Schmerzlevel (0–10)",
    xvitalPain: "Schmerz",
    xvitalPainLocation: "Schmerzort",
    xvitalPainQualitySelect: "Schmerzqualität wählen",
    xvitalPainSharp: "Stechend",
    xvitalPainDull: "Dumpf",
    xvitalPainBurning: "Brennend",
    xvitalPainThrobbing: "Pochend",
    xvitalPainCramping: "Krampfartig",
    xvitalPainAching: "Ziehend",
    xvitalMentalStatus: "Bewusstseinslage",
    xvitalAlertness: "Vigilanz",
    xvitalAlert: "Wach",
    xvitalLethargic: "Lethargisch",
    xvitalObtunded: "Benommen",
    xvitalStuporous: "Stuporös",
    xvitalComatose: "Komatös",
    xvitalMood: "Stimmungslage",
    xvitalMoodNormal: "Normal",
    xvitalMoodAnxious: "Ängstlich",
    xvitalMoodDepressed: "Deprimiert",
    xvitalMoodAgitated: "Unruhig",
    xvitalMoodEuphoric: "Euphorisch",
    xvitalMoodOther: "Andere",
    xvitalOrientation: "Orientierung",
    xvitalOrientPerson: "Person",
    xvitalOrientPlace: "Ort",
    xvitalOrientTime: "Zeit",
    xvitalOrientSituation: "Situation",
    xvitalNotes: "Zusätzliche Notizen",
    xvitalNotesPlaceholder:
      "Besondere Beobachtungen, Umstände der Messung, etc.",
    xvitalRemove: "Messung entfernen",
    xvitalAdd: "Neue Vitalzeichen-Messung hinzufügen",
    xvitalEmpty: "Keine erweiterten Vitalzeichen dokumentiert",
    xvitalMeasurementOf: "Messung vom {date}",
    xvitalBy: "von {name}",
    xvitalBpOrder: "Systolischer Wert muss höher als diastolischer Wert sein",
    xvitalHeartRatePlaceholder: "z. B. 72",

    emcTitle: "Notfallkontakte",
    emcRelSpouse: "Ehepartner/in",
    emcRelPartner: "Lebenspartner/in",
    emcRelParent: "Elternteil",
    emcRelChild: "Kind",
    emcRelSibling: "Geschwister",
    emcRelGuardian: "Vormund",
    emcRelFriend: "Freund/in",
    emcRelOther: "Sonstige",
    emcPrimaryLabel: "Hauptkontakt (wird zuerst benachrichtigt)",
    emcRemove: "Kontakt entfernen",
    emcNameLabel: "Name *",
    emcNamePlaceholder: "Vollständiger Name",
    emcRelationshipLabel: "Beziehung *",
    emcRelationshipSpecify: "Beziehung spezifizieren",
    emcRelationshipPlaceholder: "z. B. Nachbar, Kollege",
    emcPhoneLabel: "Telefonnummer *",
    emcPhonePlaceholder: "+49 30 1234567",
    emcAltPhoneLabel: "Alternative Telefonnummer",
    emcAltPhonePlaceholder: "Zweite Nummer (optional)",
    emcEmailLabel: "E-Mail",
    emcLanguageLabel: "Sprache",
    emcCanDecide: "Darf medizinische Entscheidungen treffen",
    emcAddressLabel: "Adresse (optional)",
    emcStreetPlaceholder: "Straße und Hausnummer",
    emcCityPlaceholder: "Stadt",
    emcPostalPlaceholder: "PLZ",
    emcCountryPlaceholder: "Land",
    emcNotesLabel: "Zusätzliche Notizen",
    emcNotesPlaceholder: "Besondere Informationen zu diesem Kontakt",
    emcAdd: "Neuen Notfallkontakt hinzufügen",
    emcEmpty: "Keine Notfallkontakte hinterlegt",

    recsSeverityCritical: "Kritisch",
    recsSeverityHigh: "Hoch",
    recsSeverityMedium: "Mittel",
    recsSeverityLow: "Niedrig",
    recsSeverityInfo: "Info",
    recsRiskLow: "Niedrig",
    recsRiskModerate: "Mäßig",
    recsRiskHigh: "Hoch",
    recsRiskCritical: "Kritisch",
    recsTravelCruise: "Kreuzfahrt",
    recsTravelFlight: "Flugreise",
    recsTravelLand: "Landreise",
    recsTravelMixed: "Kombiniert",

    outbreakSeverityCritical: "Kritisch",
    outbreakSeverityHigh: "Hoch",
    outbreakSeverityMedium: "Mittel",
    outbreakSeverityLow: "Niedrig",
  },
  fr: {
    xvitalTitle: "Signes vitaux détaillés",
    xvitalDateLabel: "Date de la mesure *",
    xvitalRecordedBy: "Mesuré par",
    xvitalRecordedByPlaceholder: "Nom de la personne qui mesure",
    xvitalTemperature: "Température corporelle",
    xvitalValue: "Valeur",
    xvitalSiteOral: "Buccale",
    xvitalSiteEar: "Auriculaire",
    xvitalSiteForehead: "Frontale",
    xvitalSiteArmpit: "Axillaire",
    xvitalSiteRectal: "Rectale",
    xvitalRespRate: "Fréquence respiratoire",
    xvitalBreathsPerMin: "respirations/min",
    xvitalMethodObserved: "Observée",
    xvitalMethodMeasured: "Mesurée",
    xvitalMeasuredSuffix: "mesurée",
    xvitalO2Sat: "Saturation en oxygène",
    xvitalRoomAir: "Air ambiant (sans oxygène d'appoint)",
    xvitalO2Flow: "Débit d'O2 (L/min)",
    xvitalGlucose: "Glycémie",
    xvitalGlucoseAbbr: "Glycémie",
    xvitalTimingFasting: "À jeun",
    xvitalTimingRandom: "Aléatoire",
    xvitalTimingPostprandial: "Après un repas",
    xvitalTimingBedtime: "Au coucher",
    xvitalPainLevel: "Niveau de douleur (0–10)",
    xvitalPain: "Douleur",
    xvitalPainLocation: "Localisation de la douleur",
    xvitalPainQualitySelect: "Choisir le type de douleur",
    xvitalPainSharp: "Aiguë",
    xvitalPainDull: "Sourde",
    xvitalPainBurning: "Brûlante",
    xvitalPainThrobbing: "Pulsatile",
    xvitalPainCramping: "Crampes",
    xvitalPainAching: "Lancinante",
    xvitalMentalStatus: "État de conscience",
    xvitalAlertness: "Vigilance",
    xvitalAlert: "Éveillé",
    xvitalLethargic: "Léthargique",
    xvitalObtunded: "Obnubilé",
    xvitalStuporous: "Stuporeux",
    xvitalComatose: "Comateux",
    xvitalMood: "Humeur",
    xvitalMoodNormal: "Normale",
    xvitalMoodAnxious: "Anxieux",
    xvitalMoodDepressed: "Déprimé",
    xvitalMoodAgitated: "Agité",
    xvitalMoodEuphoric: "Euphorique",
    xvitalMoodOther: "Autre",
    xvitalOrientation: "Orientation",
    xvitalOrientPerson: "Personne",
    xvitalOrientPlace: "Lieu",
    xvitalOrientTime: "Temps",
    xvitalOrientSituation: "Situation",
    xvitalNotes: "Notes complémentaires",
    xvitalNotesPlaceholder:
      "Observations particulières, circonstances de la mesure, etc.",
    xvitalRemove: "Supprimer la mesure",
    xvitalAdd: "Ajouter une mesure des signes vitaux",
    xvitalEmpty: "Aucun signe vital détaillé enregistré",
    xvitalMeasurementOf: "Mesure du {date}",
    xvitalBy: "par {name}",
    xvitalBpOrder:
      "La valeur systolique doit être supérieure à la valeur diastolique",
    xvitalHeartRatePlaceholder: "p. ex. 72",

    emcTitle: "Contacts d'urgence",
    emcRelSpouse: "Conjoint(e)",
    emcRelPartner: "Partenaire",
    emcRelParent: "Parent",
    emcRelChild: "Enfant",
    emcRelSibling: "Frère/sœur",
    emcRelGuardian: "Tuteur/tutrice",
    emcRelFriend: "Ami(e)",
    emcRelOther: "Autre",
    emcPrimaryLabel: "Contact principal (prévenu en premier)",
    emcRemove: "Supprimer le contact",
    emcNameLabel: "Nom *",
    emcNamePlaceholder: "Nom complet",
    emcRelationshipLabel: "Lien *",
    emcRelationshipSpecify: "Préciser le lien",
    emcRelationshipPlaceholder: "p. ex. voisin, collègue",
    emcPhoneLabel: "Numéro de téléphone *",
    emcPhonePlaceholder: "+33 1 23 45 67 89",
    emcAltPhoneLabel: "Autre numéro de téléphone",
    emcAltPhonePlaceholder: "Second numéro (facultatif)",
    emcEmailLabel: "E-mail",
    emcLanguageLabel: "Langue",
    emcCanDecide: "Peut prendre des décisions médicales",
    emcAddressLabel: "Adresse (facultatif)",
    emcStreetPlaceholder: "Numéro et rue",
    emcCityPlaceholder: "Ville",
    emcPostalPlaceholder: "Code postal",
    emcCountryPlaceholder: "Pays",
    emcNotesLabel: "Notes complémentaires",
    emcNotesPlaceholder: "Informations particulières sur ce contact",
    emcAdd: "Ajouter un contact d'urgence",
    emcEmpty: "Aucun contact d'urgence enregistré",

    recsSeverityCritical: "Critique",
    recsSeverityHigh: "Élevé",
    recsSeverityMedium: "Moyen",
    recsSeverityLow: "Faible",
    recsSeverityInfo: "Info",
    recsRiskLow: "Faible",
    recsRiskModerate: "Modéré",
    recsRiskHigh: "Élevé",
    recsRiskCritical: "Critique",
    recsTravelCruise: "Croisière",
    recsTravelFlight: "Avion",
    recsTravelLand: "Voie terrestre",
    recsTravelMixed: "Mixte",

    outbreakSeverityCritical: "Critique",
    outbreakSeverityHigh: "Élevée",
    outbreakSeverityMedium: "Moyenne",
    outbreakSeverityLow: "Faible",
  },
  es: {
    xvitalTitle: "Signos vitales ampliados",
    xvitalDateLabel: "Fecha de la medición *",
    xvitalRecordedBy: "Medido por",
    xvitalRecordedByPlaceholder: "Nombre de quien mide",
    xvitalTemperature: "Temperatura corporal",
    xvitalValue: "Valor",
    xvitalSiteOral: "Oral",
    xvitalSiteEar: "Oído",
    xvitalSiteForehead: "Frente",
    xvitalSiteArmpit: "Axila",
    xvitalSiteRectal: "Rectal",
    xvitalRespRate: "Frecuencia respiratoria",
    xvitalBreathsPerMin: "respiraciones/min",
    xvitalMethodObserved: "Observada",
    xvitalMethodMeasured: "Medida",
    xvitalMeasuredSuffix: "medida",
    xvitalO2Sat: "Saturación de oxígeno",
    xvitalRoomAir: "Aire ambiente (sin oxígeno suplementario)",
    xvitalO2Flow: "Flujo de O2 (L/min)",
    xvitalGlucose: "Glucosa en sangre",
    xvitalGlucoseAbbr: "Glucosa",
    xvitalTimingFasting: "En ayunas",
    xvitalTimingRandom: "Aleatoria",
    xvitalTimingPostprandial: "Después de comer",
    xvitalTimingBedtime: "Antes de dormir",
    xvitalPainLevel: "Nivel de dolor (0–10)",
    xvitalPain: "Dolor",
    xvitalPainLocation: "Localización del dolor",
    xvitalPainQualitySelect: "Seleccione el tipo de dolor",
    xvitalPainSharp: "Punzante",
    xvitalPainDull: "Sordo",
    xvitalPainBurning: "Ardiente",
    xvitalPainThrobbing: "Pulsátil",
    xvitalPainCramping: "Tipo cólico",
    xvitalPainAching: "Persistente",
    xvitalMentalStatus: "Estado de conciencia",
    xvitalAlertness: "Nivel de alerta",
    xvitalAlert: "Alerta",
    xvitalLethargic: "Letárgico",
    xvitalObtunded: "Obnubilado",
    xvitalStuporous: "Estuporoso",
    xvitalComatose: "Comatoso",
    xvitalMood: "Estado de ánimo",
    xvitalMoodNormal: "Normal",
    xvitalMoodAnxious: "Ansioso",
    xvitalMoodDepressed: "Deprimido",
    xvitalMoodAgitated: "Agitado",
    xvitalMoodEuphoric: "Eufórico",
    xvitalMoodOther: "Otro",
    xvitalOrientation: "Orientación",
    xvitalOrientPerson: "Persona",
    xvitalOrientPlace: "Lugar",
    xvitalOrientTime: "Tiempo",
    xvitalOrientSituation: "Situación",
    xvitalNotes: "Notas adicionales",
    xvitalNotesPlaceholder:
      "Observaciones especiales, circunstancias de la medición, etc.",
    xvitalRemove: "Eliminar medición",
    xvitalAdd: "Añadir nueva medición de signos vitales",
    xvitalEmpty: "No hay signos vitales ampliados registrados",
    xvitalMeasurementOf: "Medición del {date}",
    xvitalBy: "por {name}",
    xvitalBpOrder: "El valor sistólico debe ser mayor que el diastólico",
    xvitalHeartRatePlaceholder: "p. ej., 72",

    emcTitle: "Contactos de emergencia",
    emcRelSpouse: "Cónyuge",
    emcRelPartner: "Pareja",
    emcRelParent: "Padre/madre",
    emcRelChild: "Hijo/a",
    emcRelSibling: "Hermano/a",
    emcRelGuardian: "Tutor/a",
    emcRelFriend: "Amigo/a",
    emcRelOther: "Otro",
    emcPrimaryLabel: "Contacto principal (se avisa primero)",
    emcRemove: "Eliminar contacto",
    emcNameLabel: "Nombre *",
    emcNamePlaceholder: "Nombre completo",
    emcRelationshipLabel: "Relación *",
    emcRelationshipSpecify: "Especificar relación",
    emcRelationshipPlaceholder: "p. ej., vecino, compañero de trabajo",
    emcPhoneLabel: "Número de teléfono *",
    emcPhonePlaceholder: "+34 612 345 678",
    emcAltPhoneLabel: "Teléfono alternativo",
    emcAltPhonePlaceholder: "Segundo número (opcional)",
    emcEmailLabel: "Correo electrónico",
    emcLanguageLabel: "Idioma",
    emcCanDecide: "Puede tomar decisiones médicas",
    emcAddressLabel: "Dirección (opcional)",
    emcStreetPlaceholder: "Calle y número",
    emcCityPlaceholder: "Ciudad",
    emcPostalPlaceholder: "Código postal",
    emcCountryPlaceholder: "País",
    emcNotesLabel: "Notas adicionales",
    emcNotesPlaceholder: "Información especial sobre este contacto",
    emcAdd: "Añadir contacto de emergencia",
    emcEmpty: "No hay contactos de emergencia registrados",

    recsSeverityCritical: "Crítica",
    recsSeverityHigh: "Alta",
    recsSeverityMedium: "Media",
    recsSeverityLow: "Baja",
    recsSeverityInfo: "Info",
    recsRiskLow: "Bajo",
    recsRiskModerate: "Moderado",
    recsRiskHigh: "Alto",
    recsRiskCritical: "Crítico",
    recsTravelCruise: "Crucero",
    recsTravelFlight: "Avión",
    recsTravelLand: "Por tierra",
    recsTravelMixed: "Mixto",

    outbreakSeverityCritical: "Crítica",
    outbreakSeverityHigh: "Alta",
    outbreakSeverityMedium: "Media",
    outbreakSeverityLow: "Baja",
  },
  pt: {
    xvitalTitle: "Sinais vitais detalhados",
    xvitalDateLabel: "Data da medição *",
    xvitalRecordedBy: "Medido por",
    xvitalRecordedByPlaceholder: "Nome de quem mede",
    xvitalTemperature: "Temperatura corporal",
    xvitalValue: "Valor",
    xvitalSiteOral: "Oral",
    xvitalSiteEar: "Ouvido",
    xvitalSiteForehead: "Testa",
    xvitalSiteArmpit: "Axila",
    xvitalSiteRectal: "Retal",
    xvitalRespRate: "Frequência respiratória",
    xvitalBreathsPerMin: "ciclos/min",
    xvitalMethodObserved: "Observada",
    xvitalMethodMeasured: "Medida",
    xvitalMeasuredSuffix: "medida",
    xvitalO2Sat: "Saturação de oxigénio",
    xvitalRoomAir: "Ar ambiente (sem oxigénio suplementar)",
    xvitalO2Flow: "Débito de O2 (L/min)",
    xvitalGlucose: "Glicemia",
    xvitalGlucoseAbbr: "Glicemia",
    xvitalTimingFasting: "Em jejum",
    xvitalTimingRandom: "Ocasional",
    xvitalTimingPostprandial: "Após a refeição",
    xvitalTimingBedtime: "Ao deitar",
    xvitalPainLevel: "Nível de dor (0–10)",
    xvitalPain: "Dor",
    xvitalPainLocation: "Localização da dor",
    xvitalPainQualitySelect: "Selecione o tipo de dor",
    xvitalPainSharp: "Aguda",
    xvitalPainDull: "Surda",
    xvitalPainBurning: "Ardente",
    xvitalPainThrobbing: "Latejante",
    xvitalPainCramping: "Tipo cólica",
    xvitalPainAching: "Persistente",
    xvitalMentalStatus: "Estado de consciência",
    xvitalAlertness: "Vigília",
    xvitalAlert: "Alerta",
    xvitalLethargic: "Letárgico",
    xvitalObtunded: "Obnubilado",
    xvitalStuporous: "Estuporoso",
    xvitalComatose: "Comatoso",
    xvitalMood: "Humor",
    xvitalMoodNormal: "Normal",
    xvitalMoodAnxious: "Ansioso",
    xvitalMoodDepressed: "Deprimido",
    xvitalMoodAgitated: "Agitado",
    xvitalMoodEuphoric: "Eufórico",
    xvitalMoodOther: "Outro",
    xvitalOrientation: "Orientação",
    xvitalOrientPerson: "Pessoa",
    xvitalOrientPlace: "Lugar",
    xvitalOrientTime: "Tempo",
    xvitalOrientSituation: "Situação",
    xvitalNotes: "Notas adicionais",
    xvitalNotesPlaceholder:
      "Observações especiais, circunstâncias da medição, etc.",
    xvitalRemove: "Remover medição",
    xvitalAdd: "Adicionar nova medição de sinais vitais",
    xvitalEmpty: "Nenhum sinal vital detalhado registado",
    xvitalMeasurementOf: "Medição de {date}",
    xvitalBy: "por {name}",
    xvitalBpOrder: "O valor sistólico tem de ser superior ao diastólico",
    xvitalHeartRatePlaceholder: "p. ex. 72",

    emcTitle: "Contactos de emergência",
    emcRelSpouse: "Cônjuge",
    emcRelPartner: "Companheiro/a",
    emcRelParent: "Pai/mãe",
    emcRelChild: "Filho/a",
    emcRelSibling: "Irmão/irmã",
    emcRelGuardian: "Tutor/a",
    emcRelFriend: "Amigo/a",
    emcRelOther: "Outro",
    emcPrimaryLabel: "Contacto principal (avisado primeiro)",
    emcRemove: "Remover contacto",
    emcNameLabel: "Nome *",
    emcNamePlaceholder: "Nome completo",
    emcRelationshipLabel: "Relação *",
    emcRelationshipSpecify: "Especificar relação",
    emcRelationshipPlaceholder: "p. ex. vizinho, colega",
    emcPhoneLabel: "Número de telefone *",
    emcPhonePlaceholder: "+351 912 345 678",
    emcAltPhoneLabel: "Telefone alternativo",
    emcAltPhonePlaceholder: "Segundo número (opcional)",
    emcEmailLabel: "E-mail",
    emcLanguageLabel: "Idioma",
    emcCanDecide: "Pode tomar decisões médicas",
    emcAddressLabel: "Morada (opcional)",
    emcStreetPlaceholder: "Rua e número",
    emcCityPlaceholder: "Localidade",
    emcPostalPlaceholder: "Código postal",
    emcCountryPlaceholder: "País",
    emcNotesLabel: "Notas adicionais",
    emcNotesPlaceholder: "Informações especiais sobre este contacto",
    emcAdd: "Adicionar contacto de emergência",
    emcEmpty: "Nenhum contacto de emergência registado",

    recsSeverityCritical: "Crítica",
    recsSeverityHigh: "Alta",
    recsSeverityMedium: "Média",
    recsSeverityLow: "Baixa",
    recsSeverityInfo: "Info",
    recsRiskLow: "Baixo",
    recsRiskModerate: "Moderado",
    recsRiskHigh: "Alto",
    recsRiskCritical: "Crítico",
    recsTravelCruise: "Cruzeiro",
    recsTravelFlight: "Avião",
    recsTravelLand: "Por terra",
    recsTravelMixed: "Misto",

    outbreakSeverityCritical: "Crítica",
    outbreakSeverityHigh: "Alta",
    outbreakSeverityMedium: "Média",
    outbreakSeverityLow: "Baixa",
  },
};
